import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { useAuthStore } from '@store/auth';

/**
 * AI Rehberi — gamify edilmiş onboarding tutorial'ı.
 * Yeni kullanıcıyı kendi gerçek verisi üzerinde ilk kritik aksiyonlardan
 * geçiren, ilerleme çubuklu interaktif kontrol listesi.
 *
 * Tamamlanma durumu KULLANICI BAŞINA saklanır. Dashboard layout gibi bir
 * backend tercihi de mümkündü; ancak yeni tablo/endpoint gerektirdiği için
 * ilk aşamada kullanıcı id'sine göre localStorage yeterli (görev notu).
 * Şema hazır olduğunda `tamamla`/`sifirla` aksiyonları bir API mutation'ına
 * bağlanabilir; UI değişmez.
 */

export type RehberAdimId = 'musteri' | 'kartvizit' | 'aiMail' | 'rapor';

export interface RehberAdimTanim {
  id: RehberAdimId;
  /** i18n key (rehber namespace). defaultValue ile birlikte kullanılır. */
  i18n: string;
  /** Namespace yüklenmeden önce gösterilecek Türkçe yedek metin. */
  baslik: string;
  aciklama: string;
  /** Tıklanınca yönlendirilecek route (query param'lı deep-link). */
  hedef: string;
  /**
   * Özellik henüz tam kurulmadıysa (stub) true. UI'da küçük bir "yakında"
   * rozeti gösterilir ve rapor/analiz için manuel tamamlama beklenir.
   */
  stub?: boolean;
}

/** Adım sırası + deep-link tanımları. */
export const REHBER_ADIMLAR: RehberAdimTanim[] = [
  {
    id: 'musteri',
    i18n: 'adim.musteri.baslik',
    baslik: 'İlk müşterini ekle',
    aciklama: 'CRM’in kalbi müşterilerdir. İlk kaydını oluştur.',
    hedef: '/musteriler?rehber=yeni',
  },
  {
    id: 'kartvizit',
    i18n: 'adim.kartvizit.baslik',
    baslik: 'İlk kartvizitini tara → otomatik kayıt',
    aciklama: 'Bir kartvizit fotoğrafı çek, alanlar otomatik dolsun.',
    hedef: '/musteriler?rehber=kartvizit',
  },
  {
    id: 'aiMail',
    i18n: 'adim.aiMail.baslik',
    baslik: 'İlk takip mailini AI’a yazdır',
    aciklama: 'Müşterine profesyonel bir takip e-postası gönder.',
    hedef: '/epostalar?rehber=compose',
  },
  {
    id: 'rapor',
    i18n: 'adim.rapor.baslik',
    baslik: 'İlk raporunu al',
    aciklama: 'Dashboard’da “Bu ayın raporunu çıkar” ile AI raporunu üret.',
    hedef: '/dashboard?rehber=rapor',
  },
];

export const REHBER_ADIM_IDLER = REHBER_ADIMLAR.map((a) => a.id);

interface RehberKullaniciDurum {
  /** Tamamlanan adımlar. */
  tamamlanan: Partial<Record<RehberAdimId, boolean>>;
  /** Kutlama (100%) kullanıcı tarafından kapatıldı mı. */
  kutlamaKapatildi: boolean;
  /** Rehber girişi tamamen gizlensin mi (kullanıcı "kapat" dedi). */
  gizli: boolean;
}

const BOS_DURUM: RehberKullaniciDurum = {
  tamamlanan: {},
  kutlamaKapatildi: false,
  gizli: false,
};

interface RehberStore {
  /** userId → durum. Tek localStorage key, kullanıcı başına slice. */
  byUser: Record<string, RehberKullaniciDurum>;
  tamamla: (userId: string, adim: RehberAdimId) => void;
  geriAl: (userId: string, adim: RehberAdimId) => void;
  sifirla: (userId: string) => void;
  kutlamaKapat: (userId: string) => void;
  rehberiGizle: (userId: string) => void;
}

function slice(state: RehberStore, userId: string): RehberKullaniciDurum {
  return state.byUser[userId] ?? BOS_DURUM;
}

export const useRehberStore = create<RehberStore>()(
  persist(
    (set) => ({
      byUser: {},
      tamamla: (userId, adim) =>
        set((s) => {
          const mevcut = slice(s, userId);
          if (mevcut.tamamlanan[adim]) return s; // no-op: zaten tamam
          return {
            byUser: {
              ...s.byUser,
              [userId]: {
                ...mevcut,
                tamamlanan: { ...mevcut.tamamlanan, [adim]: true },
              },
            },
          };
        }),
      geriAl: (userId, adim) =>
        set((s) => {
          const mevcut = slice(s, userId);
          const yeni = { ...mevcut.tamamlanan };
          delete yeni[adim];
          return {
            byUser: {
              ...s.byUser,
              [userId]: { ...mevcut, tamamlanan: yeni, kutlamaKapatildi: false },
            },
          };
        }),
      sifirla: (userId) =>
        set((s) => ({
          byUser: { ...s.byUser, [userId]: { ...BOS_DURUM } },
        })),
      kutlamaKapat: (userId) =>
        set((s) => ({
          byUser: {
            ...s.byUser,
            [userId]: { ...slice(s, userId), kutlamaKapatildi: true },
          },
        })),
      rehberiGizle: (userId) =>
        set((s) => ({
          byUser: {
            ...s.byUser,
            [userId]: { ...slice(s, userId), gizli: true },
          },
        })),
    }),
    {
      name: 'zoprey-ai-rehberi',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ byUser: state.byUser }),
    },
  ),
);

/** O anki kullanıcının rehber durumunu okuyan yardımcı hook. */
export function useRehberDurum() {
  const userId = useAuthStore((s) => s.me?.userId) ?? null;
  const durum = useRehberStore((s) =>
    userId ? s.byUser[userId] ?? BOS_DURUM : BOS_DURUM,
  );

  const tamamlananSayi = REHBER_ADIM_IDLER.filter(
    (id) => durum.tamamlanan[id],
  ).length;
  const toplam = REHBER_ADIM_IDLER.length;
  const yuzde = Math.round((tamamlananSayi / toplam) * 100);
  const bitti = tamamlananSayi === toplam;

  return { userId, durum, tamamlananSayi, toplam, yuzde, bitti };
}

/**
 * Uygulamanın herhangi bir yerinden (deep-link'lenen aksiyon gerçekleşince)
 * bir adımı tamamlamak için çağrılır. userId otomatik auth store'dan alınır.
 * Örn: kartvizit taraması sonuç verince, e-posta gönderilince.
 */
export function rehberTamamla(adim: RehberAdimId): void {
  const userId = useAuthStore.getState().me?.userId;
  if (!userId) return;
  useRehberStore.getState().tamamla(userId, adim);
}
